<template>
  <Teleport to="body">
    <ClientOnly>
      <!-- Desktop panel -->
      <aside
        v-if="!isMobile"
        class="chat-panel fixed top-0 right-0 h-screen z-40 flex flex-col transition-[width] duration-200 ease-out"
        :class="isOpen ? 'w-[360px]' : 'w-[56px]'"
        role="complementary"
        :aria-label="$t('chat.title')"
      >
        <!-- Persistent online strip — hugs the panel's LEFT edge via
             `right-full`. Space for it is reserved globally (STRIP_W folded
             into --chat-panel-width in useChatUI). Do NOT add overflow-hidden
             to this aside or the strip would be clipped. -->
        <ChatOnlineStrip />

        <!-- Header -->
        <div class="flex items-center justify-between px-3 h-14 border-b border-white/5 shrink-0">
          <!-- Identity block: avatar + name + status picker. Occupies the
               left side when the panel is expanded. -->
          <div v-if="isOpen" class="flex items-center gap-2 min-w-0 flex-1">
            <ChatStatusPicker />
            <div class="min-w-0">
              <p class="text-sm text-white font-semibold truncate leading-tight">
                {{ displayName || $t('chat.title') }}
              </p>
              <div class="flex items-center gap-1.5 text-[10px] leading-tight">
                <span v-if="status === 'open'" class="text-text-muted">
                  {{ $t('chat.status.self.' + selfStatus) }}
                </span>
                <span v-else class="text-amber-300">{{ statusLabel }}</span>
                <span v-if="myChatId" class="text-text-dim">·</span>
                <button
                  v-if="myChatId"
                  type="button"
                  class="font-mono text-text-dim hover:text-white tracking-wider inline-flex items-center gap-1 group"
                  :title="$t('chat.settings.chat_id_copy')"
                  @click="copyChatId"
                >
                  <span>{{ formattedChatId }}</span>
                  <el-icon size="9" class="opacity-0 group-hover:opacity-100 transition-opacity">
                    <DocumentCopy v-if="!chatIdCopied" />
                    <Check v-else />
                  </el-icon>
                </button>
                <span v-if="chatIdCopied" class="text-emerald-400">{{ $t('chat.settings.chat_id_copied') }}</span>
              </div>
            </div>
          </div>
          <div class="flex items-center gap-1">
            <button
              v-if="isOpen"
              type="button"
              class="p-1.5 rounded hover:bg-white/5 text-text-muted hover:text-white transition-colors"
              :title="$t('chat.settings.title')"
              :aria-label="$t('chat.settings.title')"
              @click="settingsOpen = true"
            >
              <el-icon size="14"><Setting /></el-icon>
            </button>
            <button
              type="button"
              class="p-1.5 rounded hover:bg-white/5 text-text-muted hover:text-white transition-colors"
              :title="isOpen ? $t('chat.actions.collapse') : $t('chat.actions.expand')"
              :aria-label="isOpen ? $t('chat.actions.collapse') : $t('chat.actions.expand')"
              @click="toggle"
            >
              <el-icon size="16">
                <ArrowRight v-if="isOpen" />
                <ChatDotRound v-else />
              </el-icon>
            </button>
          </div>
        </div>

        <!-- Collapsed sliver: icon-only tabs with unread badges -->
        <div v-if="!isOpen" class="flex flex-col items-center gap-1 py-2" role="tablist">
          <button
            v-for="t in tabList"
            :key="t.key"
            type="button"
            class="relative w-10 h-10 rounded-lg flex items-center justify-center text-text-muted hover:text-white hover:bg-white/5 transition-colors"
            :class="activeTab === t.key ? 'bg-white/5 text-white' : ''"
            :title="t.label"
            :aria-label="t.label"
            role="tab"
            :aria-selected="activeTab === t.key"
            @click="setTab(t.key)"
          >
            <el-icon size="16"><component :is="t.icon" /></el-icon>
            <span
              v-if="t.badge > 0"
              class="absolute top-1 right-1 min-w-[14px] h-[14px] px-1 rounded-full bg-primary text-black text-[9px] font-bold flex items-center justify-center"
            >
              {{ t.badge > 9 ? '9+' : t.badge }}
            </span>
          </button>
        </div>

        <!-- Expanded: full tabs + content -->
        <template v-else>
          <div class="flex border-b border-white/5 shrink-0" role="tablist">
            <button
              v-for="t in tabList"
              :key="t.key"
              type="button"
              class="relative flex-1 px-2 py-2 text-[10px] uppercase tracking-widest font-semibold transition-colors"
              :class="activeTab === t.key
                ? 'text-white border-b-2 border-primary'
                : 'text-text-muted hover:text-white border-b-2 border-transparent'"
              role="tab"
              :aria-selected="activeTab === t.key"
              @click="activeTab = t.key"
            >
              <span class="inline-flex items-center gap-1.5">
                {{ t.label }}
                <span
                  v-if="t.badge > 0"
                  class="min-w-[16px] h-[16px] px-1 rounded-full bg-primary text-black text-[9px] font-bold flex items-center justify-center"
                >
                  {{ t.badge > 99 ? '99+' : t.badge }}
                </span>
              </span>
            </button>
          </div>

          <!-- Disconnect banner — shrink-0 so the surrounding flex-col
               doesn't squeeze it, min-w-0 + truncate so long i18n labels
               can't trigger horizontal scroll in the 360px panel. -->
          <Transition name="chat-banner">
            <div
              v-if="status === 'offline' || status === 'reconnecting'"
              class="shrink-0 px-3 py-1.5 bg-amber-500/10 border-b border-amber-500/20 text-[10px] text-amber-200 flex items-center justify-between gap-2 overflow-hidden"
            >
              <span class="min-w-0 truncate flex items-center gap-1.5">
                <span class="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse shrink-0" aria-hidden="true"></span>
                {{ statusLabel }}
              </span>
              <button
                v-if="status === 'offline'"
                type="button"
                class="underline shrink-0"
                @click="resume"
              >
                {{ $t('chat.status.retry') }}
              </button>
            </div>
          </Transition>

          <!-- Active tab content -->
          <div class="flex-1 min-h-0 flex flex-col">
            <ChatGlobalRoom v-if="activeTab === 'global'" />
            <ChatDMs v-else-if="activeTab === 'dms'" />
            <ChatFriends v-else-if="activeTab === 'friends'" />
            <ChatOnlinePanel v-else-if="activeTab === 'online'" />
            <ChatStaff v-else-if="activeTab === 'staff'" />
            <ChatInbox v-else-if="activeTab === 'inbox'" />
          </div>
        </template>
      </aside>

      <!-- Mobile floating bubble -->
      <button
        v-else-if="!mobileOpen"
        type="button"
        class="fixed bottom-4 right-4 z-40 w-12 h-12 rounded-full bg-primary text-black flex items-center justify-center shadow-lg"
        :aria-label="$t('chat.title')"
        @click="mobileOpen = true"
      >
        <el-icon size="20"><ChatDotRound /></el-icon>
        <span
          v-if="totalUnread > 0"
          class="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-[var(--bg-color,#030304)]"
        >
          {{ totalUnread > 99 ? '99+' : totalUnread }}
        </span>
      </button>

      <!-- Mobile overlay panel -->
      <aside
        v-else
        class="chat-panel chat-panel--mobile fixed inset-0 z-40 flex flex-col"
      >
        <div class="flex items-center justify-between px-3 h-12 border-b border-white/5 shrink-0">
          <span class="text-xs font-bold uppercase tracking-widest text-white">{{ $t('chat.title') }}</span>
          <div class="flex items-center gap-1">
            <button
              type="button"
              class="p-1.5 rounded hover:bg-white/5 text-text-muted hover:text-white transition-colors"
              :title="$t('chat.settings.title')"
              :aria-label="$t('chat.settings.title')"
              @click="settingsOpen = true"
            >
              <el-icon size="14"><Setting /></el-icon>
            </button>
            <button type="button" class="p-1.5 rounded hover:bg-white/5 text-text-muted" @click="mobileOpen = false">
              <el-icon size="16"><Close /></el-icon>
            </button>
          </div>
        </div>
        <div class="flex border-b border-white/5 shrink-0" role="tablist">
          <button
            v-for="t in tabList"
            :key="t.key"
            type="button"
            class="relative flex-1 px-2 py-2 text-[10px] uppercase tracking-widest font-semibold"
            :class="activeTab === t.key ? 'text-white border-b-2 border-primary' : 'text-text-muted border-b-2 border-transparent'"
            @click="activeTab = t.key"
          >
            <span class="inline-flex items-center gap-1.5">
              {{ t.label }}
              <span
                v-if="t.badge > 0"
                class="min-w-[16px] h-[16px] px-1 rounded-full bg-primary text-black text-[9px] font-bold flex items-center justify-center"
              >
                {{ t.badge > 99 ? '99+' : t.badge }}
              </span>
            </span>
          </button>
        </div>
        <div class="flex-1 min-h-0 flex flex-col">
          <ChatGlobalRoom v-if="activeTab === 'global'" />
          <ChatDMs v-else-if="activeTab === 'dms'" />
          <ChatFriends v-else-if="activeTab === 'friends'" />
          <ChatOnlinePanel v-else-if="activeTab === 'online'" />
          <ChatStaff v-else-if="activeTab === 'staff'" />
          <ChatInbox v-else-if="activeTab === 'inbox'" />
        </div>
      </aside>

      <!-- Chat settings dialog — rendered outside the v-if/v-else chain so it
           doesn't break adjacency; the dialog itself teleports to body. -->
      <ChatSettingsDialog v-model="settingsOpen" />

      <!-- Detached chat windows — each lives as a floating, draggable +
           resizable frame outside the main panel. Re-attach button on each
           returns it to the panel. Desktop-only; the composable auto-
           reattaches on mobile resize. -->
      <ChatDetachedWindow
        v-for="w in detachedWindows"
        :key="w.threadId"
        :state="w"
      />

      <!-- Incoming-call toast (top-right) and active-call window (floating).
           Both live at the panel level so the call UI survives tab switches
           and the panel collapse/expand. -->
      <ChatIncomingCallDialog />
      <ChatCallWindow />

      <!-- Single shared user hover-card for the online strip + Online tab.
           Teleports to body and opens to the LEFT of the hovered row. -->
      <ChatUserHoverCard />
    </ClientOnly>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { ArrowRight, ChatDotRound, User, Bell, UserFilled, Avatar, Close, Setting, DocumentCopy, Check, Connection } from '@element-plus/icons-vue'
import { useChatSocket } from '~/composables/chat/useChatSocket'
import { useChatUI, syncChatPanelWidth } from '~/composables/chat/useChatUI'
import { useChatInbox } from '~/composables/chat/useChatInbox'
import { useFriends } from '~/composables/chat/useFriends'
import { usePresence } from '~/composables/chat/usePresence'
import { useDetachedWindows } from '~/composables/chat/useDetachedWindows'

const { t } = useI18n()
const { loggedIn, user } = useUserSession()
const { isOpen, activeTab, toggle, setTab } = useChatUI()
const { status, lastError, connect, resume, stop, on: onWsFrame } = useChatSocket()
const { totalUnread } = useChatInbox()
const { incomingCount: friendRequestsCount } = useFriends()
const { selfStatus } = usePresence()
const { detached: detachedWindows } = useDetachedWindows()

// Display name — falls back through chat displayName → name → "CHAT".
const displayName = computed(() =>
  user.value?.chatProfile?.displayName ||
  user.value?.name ||
  ''
)

/* ──── Chat ID copy-button wiring (header shortcut) ──── */
//
// The chatId is minted lazily on the first WS connect and sent via a
// `identity` frame. We cache it locally so the header shows it immediately
// on subsequent renders. The formatted XXXX-XXXX display mirrors what the
// settings dialog shows — identical handling of "not yet assigned".
const myChatId = ref(user.value?.chatProfile?.chatId || null)
const chatIdCopied = ref(false)
let offIdentity = null
onMounted(() => {
  offIdentity = onWsFrame('identity', (frame) => {
    if (typeof frame?.chatId === 'string') myChatId.value = frame.chatId
  })
})
onBeforeUnmount(() => { if (offIdentity) offIdentity() })

const formattedChatId = computed(() => {
  const id = myChatId.value
  if (!id || id.length !== 8) return id || ''
  return `${id.slice(0, 4)}-${id.slice(4)}`
})

async function copyChatId() {
  const id = formattedChatId.value
  if (!id) return
  if (await copyToClipboard(id)) {
    chatIdCopied.value = true
    setTimeout(() => { chatIdCopied.value = false }, 1500)
  }
}

/**
 * Clipboard write with HTTP-friendly fallback.
 *
 * `navigator.clipboard.writeText` silently fails (throws NotAllowedError)
 * in non-secure contexts — which includes our dev hostnames on http://.
 * We fall back to the legacy `document.execCommand('copy')` via a hidden
 * textarea so it works on plain HTTP too.
 */
async function copyToClipboard(text) {
  try {
    if (typeof window !== 'undefined' && window.isSecureContext && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch { /* fall through to legacy path */ }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.top = '-9999px'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

const tabList = computed(() => [
  { key: 'global',  label: t('chat.tabs.global'),  icon: ChatDotRound, badge: 0 },
  { key: 'dms',     label: t('chat.tabs.dms'),     icon: User,         badge: totalUnread.value },
  { key: 'friends', label: t('chat.tabs.friends'), icon: UserFilled,   badge: friendRequestsCount.value },
  { key: 'online',  label: t('chat.tabs.online'),  icon: Connection,   badge: 0 },
  { key: 'staff',   label: t('chat.tabs.staff'),   icon: Avatar,       badge: 0 },
  { key: 'inbox',   label: t('chat.tabs.inbox'),   icon: Bell,         badge: totalUnread.value },
])

const statusLabel = computed(() => {
  if (status.value === 'connecting') return t('chat.status.connecting')
  if (status.value === 'reconnecting') return t('chat.status.reconnecting')
  if (status.value === 'offline') return t('chat.status.offline')
  return ''
})

const statusDotClass = computed(() => {
  if (status.value === 'open') return 'bg-emerald-400'
  if (status.value === 'reconnecting' || status.value === 'connecting') return 'bg-amber-400 animate-pulse'
  return 'bg-red-400'
})

// Mobile detection
const isMobile = ref(false)
const mobileOpen = ref(false)

// Chat settings dialog (auto-translate language etc.)
const settingsOpen = ref(false)
function syncMobile() { isMobile.value = window.matchMedia('(max-width: 768px)').matches }

onMounted(() => {
  if (!loggedIn.value) return
  syncMobile()
  window.addEventListener('resize', syncMobile)
  // Re-assert the layout gutter. onBeforeUnmount clears this var, and the
  // useChatUI watcher that owns it only re-fires on an isOpen toggle — so a
  // remount without a toggle (returning from /onboarding, where showChat
  // hides the panel) would leave the var cleared and the fixed panel would
  // overlap the main content. Idempotent; safe alongside the watcher.
  syncChatPanelWidth()
  connect()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', syncMobile)
  stop()
  // Clear the CSS var so a layout with no chat panel reclaims the gutter.
  document.documentElement.style.removeProperty('--chat-panel-width')
})

// Keyboard: Esc closes the panel
function onKey(e) {
  if (e.key === 'Escape') {
    if (isMobile.value && mobileOpen.value) { mobileOpen.value = false; return }
    if (isOpen.value) toggle()
  }
}
onMounted(() => window.addEventListener('keydown', onKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))

// Reconnect after login state change
watch(loggedIn, (v) => {
  if (v) connect()
  else stop()
})
</script>

<style scoped>
/* Glass chrome — shared with AdminSidebar + SharedTopNavbar via the
   `--chrome-*` tokens. The three rails read as one continuous frame
   around the page content instead of three opaque slabs. */
.chat-panel {
  background: var(--chrome-bg, rgba(13, 14, 18, 0.65));
  backdrop-filter: blur(var(--chrome-blur, 24px));
  -webkit-backdrop-filter: blur(var(--chrome-blur, 24px));
  border-left: 1px solid var(--chrome-border, rgba(255, 255, 255, 0.06));
}
/* Mobile variant covers the full viewport — no left border, the
   panel IS the surface. */
.chat-panel--mobile {
  border-left: none;
}

.scrollbar-thin::-webkit-scrollbar { width: 6px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 3px; }
.scrollbar-thin::-webkit-scrollbar-track { background: transparent; }

/* Smooth slide for the disconnect banner — avoids the "jolt" when it
   appears/disappears mid-layout. */
.chat-banner-enter-active,
.chat-banner-leave-active {
  transition: opacity 160ms ease, max-height 200ms ease;
  overflow: hidden;
}
.chat-banner-enter-from,
.chat-banner-leave-to {
  opacity: 0;
  max-height: 0;
}
.chat-banner-enter-to,
.chat-banner-leave-from {
  opacity: 1;
  max-height: 40px;
}
</style>
